import { createFeatureSelector, createSelector } from '@ngrx/store';
import { ShoppingListStore } from './shopping-list.store';
import { ShoppingListCommentDto } from '@api/*';

export const selectShoppingListStore = createFeatureSelector<ShoppingListStore>('shoppingList');

// ==================== DATA SELECTORS ====================

export const selectShoppingLists = createSelector(selectShoppingListStore, state => state.shoppingLists);

export const selectShoppingListById = (shoppingListId: number) =>
    createSelector(selectShoppingLists, shoppingLists => shoppingLists.find(list => list.id === shoppingListId));

export const selectShoppingListProducts = (shoppingListId: number) =>
    createSelector(selectShoppingListById(shoppingListId), shoppingList => shoppingList?.shoppingListProduct ?? []);

// isAvailable is false for products the backend has deleted (kept around so the
// list line doesn't just disappear) -- exclude those from count/total.
export const selectShoppingListUnavailableProducts = (shoppingListId: number) =>
    createSelector(selectShoppingListProducts(shoppingListId), products => products.filter(product => !product.isAvailable));

export const selectShoppingListProductCount = (shoppingListId: number) =>
    createSelector(selectShoppingListProducts(shoppingListId), products =>
        products.filter(product => product.isAvailable).reduce((total, product) => total + product.quantity, 0),
    );

export const selectShoppingListTotal = (shoppingListId: number) =>
    createSelector(selectShoppingListProducts(shoppingListId), products =>
        products
            .filter(product => product.isAvailable)
            .reduce((total, product) => total + product.customerPriceExVat * product.quantity, 0),
    );

export const selectShoppingListCount = createSelector(selectShoppingLists, shoppingLists => shoppingLists.length);

export const selectShoppingListCommentsByRow = (shoppingListId: number) =>
    createSelector(selectShoppingListById(shoppingListId), shoppingList => {
        const commentsByRow = new Map<number, ShoppingListCommentDto[]>();

        for (const comment of shoppingList?.shoppingListComment ?? []) {
            if (!comment.shoppingListProductId) continue;

            const rowComments = commentsByRow.get(comment.shoppingListProductId) ?? [];
            rowComments.push(comment);
            commentsByRow.set(comment.shoppingListProductId, rowComments);
        }
        return commentsByRow;
    });

// ==================== GROUP SELECTORS ====================

export const selectShoppingListGroups = (shoppingListId: number) =>
    createSelector(selectShoppingListById(shoppingListId), shoppingList => shoppingList?.shoppingListGroup ?? []);

// Includes empty groups and unavailable products -- filtering/styling is left to the component.
export const selectShoppingListProductsByGroup = (shoppingListId: number) =>
    createSelector(selectShoppingListGroups(shoppingListId), selectShoppingListProducts(shoppingListId), (groups, products) =>
        groups.map(group => ({
            group,
            products: products.filter(product => product.shoppingListGroupId === group.id),
        })),
    );

// ==================== FAVORITE SELECTORS ====================

export const selectFavoriteProductIds = createSelector(
    selectShoppingLists,
    shoppingLists => new Set(shoppingLists.flatMap(list => list.shoppingListProduct ?? []).map(product => product.productId)),
);

// ==================== LOADING SELECTOR ====================

export const selectShoppingListLoading = createSelector(selectShoppingListStore, state => state.loading);

// ==================== ERROR SELECTOR ====================

export const selectShoppingListError = createSelector(selectShoppingListStore, state => state.error);
